import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, CheckCircle2, CalendarCheck } from "lucide-react";
import { supabase } from "../supabaseClient";

// ── Blocked free/personal email domains ──────────────────────────────────────
const BLOCKED_DOMAINS = [
  "gmail.com","yahoo.com","yahoo.co.in","yahoo.co.uk","hotmail.com","hotmail.co.uk",
  "outlook.com","live.com","icloud.com","me.com","mac.com","aol.com","protonmail.com",
  "proton.me","zoho.com","yandex.com","yandex.ru","gmx.com","gmx.net","mail.com",
  "rediffmail.com","sify.com","msn.com","inbox.com","fastmail.com","tutanota.com",
];

function isCompanyEmail(email) {
  const parts = email.toLowerCase().split("@");
  if (parts.length !== 2) return false;
  return !BLOCKED_DOMAINS.includes(parts[1]);
}

// ── Geographies ───────────────────────────────────────────────────────────────
const GEOGRAPHIES = [
  "India", "United Kingdom", "UAE / Middle East",
  "Singapore / APAC", "USA", "Europe (Other)", "Other",
];

const MODULES_OF_INTEREST = [
  "FX & Hedging", "Debt Management", "Investments",
  "Receivables Intelligence", "Payables", "Cash Management",
  "Treasury Reporting", "Full Platform",
];

// ── Demo Request Modal ────────────────────────────────────────────────────────
function DemoModal({ onClose }) {
  const [name,       setName]       = useState("");
  const [company,    setCompany]    = useState("");
  const [role,       setRole]       = useState("");
  const [email,      setEmail]      = useState("");
  const [geography,  setGeography]  = useState("");
  const [modules,    setModules]    = useState([]);
  const [budget,     setBudget]     = useState("");
  const [message,    setMessage]    = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading,    setLoading]    = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [error,      setError]      = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
const [captchaError,  setCaptchaError]  = useState(false);
const CAPTCHA_QUESTIONS = [
  { q: "12 + 19 = ?",   a: 31  },
  { q: "45 - 13 = ?",   a: 32  },
  { q: "8 × 7 = ?",     a: 56  },
  { q: "81 ÷ 9 = ?",    a: 9   },
  { q: "23 + 34 = ?",   a: 57  },
  { q: "100 - 37 = ?",  a: 63  },
  { q: "6 × 8 = ?",     a: 48  },
  { q: "72 ÷ 8 = ?",    a: 9   },
  { q: "15 + 48 = ?",   a: 63  },
  { q: "94 - 46 = ?",   a: 48  },
  { q: "9 × 6 = ?",     a: 54  },
  { q: "64 ÷ 8 = ?",    a: 8   },
  { q: "37 + 55 = ?",   a: 92  },
  { q: "120 - 45 = ?",  a: 75  },
  { q: "7 × 9 = ?",     a: 63  },
  { q: "96 ÷ 12 = ?",   a: 8   },
  { q: "28 + 67 = ?",   a: 95  },
  { q: "150 - 88 = ?",  a: 62  },
  { q: "11 × 8 = ?",    a: 88  },
  { q: "144 ÷ 12 = ?",  a: 12  },
];

const [captcha] = useState(
  () => CAPTCHA_QUESTIONS[Math.floor(Math.random() * CAPTCHA_QUESTIONS.length)]
);

  function handleEmailBlur() {
    if (!email) { setEmailError(""); return; }
    if (!isCompanyEmail(email)) {
      setEmailError("Please use your company email address — personal email domains are not accepted.");
    } else {
      setEmailError("");
    }
  }

  function toggleModule(m) {
    setModules((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
   if (parseInt(captchaAnswer) !== captcha.a) {
  setCaptchaError(true);
  return;

}
    if (!isCompanyEmail(email)) {
      setEmailError("Please use your company email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { error: insertError } = await supabase
        .from("build_requests")
        .insert([{
          name,
          company,
          role,
          email,
          geography,
          modules_of_interest: modules.length > 0 ? [...modules] : null,
          indicative_budget:   budget,
          message:             message || null,
          created_at:          new Date().toISOString(),
        }]);
      if (insertError) throw insertError;
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl border relative overflow-hidden"
        style={{ background: "#0d0e18", borderColor: "rgba(99,102,241,0.3)" }}
      >
        {/* Header bar */}
        <div className="px-8 pt-8 pb-6 border-b border-[#1e2030]">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-white" />
            </div>
            <span className="text-xs text-indigo-400 uppercase tracking-wider font-medium">
              Build Your Platform
            </span>
          </div>
          <h2 className="text-2xl font-bold text-white">Tell Us What You Want to Build</h2>
          <p className="text-gray-500 text-sm mt-1">
            A treasury practitioner will come back to you within one business day.
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
          {success ? (
            <div className="flex flex-col items-center text-center py-8 gap-4">
              <CheckCircle2 className="w-14 h-14 text-emerald-400" />
              <h3 className="text-white font-bold text-xl">Request received.</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Thank you, <span className="text-white">{name}</span>. We'll reach out to{" "}
                <span className="text-indigo-400">{email}</span> within few days to
                discuss your build.
              </p>
              <button
                onClick={onClose}
                className="mt-2 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name + Role */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Full name</label>
                  <input
                    type="text" required value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-4 py-2.5 rounded-xl bg-[#12131f] border border-[#2a2d3a] text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Role</label>
                  <input
                    type="text" required value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="CFO / Treasurer"
                    className="w-full px-4 py-2.5 rounded-xl bg-[#12131f] border border-[#2a2d3a] text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 transition-colors text-sm"
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Company</label>
                <input
                  type="text" required value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Company name"
                  className="w-full px-4 py-2.5 rounded-xl bg-[#12131f] border border-[#2a2d3a] text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 transition-colors text-sm"
                />
              </div>

              {/* Company email */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">
                  Work email
                </label>
                <input
                  type="email" required value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                  onBlur={handleEmailBlur}
                  placeholder="you@yourcompany.com"
                  className={`w-full px-4 py-2.5 rounded-xl bg-[#12131f] border text-white placeholder-gray-600 focus:outline-none transition-colors text-sm ${
                    emailError ? "border-red-500/60 focus:border-red-500" : "border-[#2a2d3a] focus:border-indigo-500"
                  }`}
                />
                {emailError ? (
                  <p className="text-red-400 text-xs mt-1.5">{emailError}</p>
                ) : (
                  <p className="text-gray-600 text-xs mt-1.5">
                    Company email required — Gmail, Yahoo and similar are not accepted.
                  </p>
                )}
              </div>

              {/* Geography */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">
                  Business geography
                </label>
                <select
                  required value={geography}
                  onChange={(e) => setGeography(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-[#12131f] border border-[#2a2d3a] text-white focus:outline-none focus:border-indigo-500 transition-colors text-sm"
                >
                  <option value="" disabled>Where is your treasury primarily based?</option>
                  {GEOGRAPHIES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              {/* Modules of interest */}
              <div>
                <label className="block text-xs text-gray-500 mb-2.5 uppercase tracking-wider">
                  Areas of interest <span className="normal-case text-gray-600">(select all that apply)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {MODULES_OF_INTEREST.map((m) => (
                    <button
                      key={m} type="button"
                      onClick={() => toggleModule(m)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        modules.includes(m)
                          ? "bg-indigo-600/30 border-indigo-500/60 text-indigo-300"
                          : "bg-transparent border-[#2a2d3a] text-gray-500 hover:border-gray-500 hover:text-gray-300"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Indicative budget */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">
                  Indicative budget <span className="text-red-400">*</span>
                </label>
                <select
                  required value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-[#12131f] border border-[#2a2d3a] text-white focus:outline-none focus:border-indigo-500 transition-colors text-sm"
                >
                  <option value="" disabled>Select your budget range</option>
                  <option value="£15k-£25k">£15,000 – £25,000</option>
                  <option value="£25k-£50k">£25,000 – £50,000</option>
                  <option value="£50k-£75k">£50,000 – £75,000</option>
                  <option value="£75k-£100k">£75,000 – £100,000</option>
                  <option value="£100K+">£100,000 + </option>
                 
                </select>
              </div>

              {/* Optional message */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">
                  Anything specific? <span className="normal-case text-gray-600">(optional)</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Tell us about your current setup, team size, or specific pain points…"
                  className="w-full px-4 py-2.5 rounded-xl bg-[#12131f] border border-[#2a2d3a] text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 transition-colors text-sm resize-none"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  {error}
                </p>
              )}
<div>
  <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">
    Quick check: {captcha.q}<span className="text-red-400">*</span>
  </label>
  <input
    type="number"
    required
    value={captchaAnswer}
    onChange={(e) => { setCaptchaAnswer(e.target.value); setCaptchaError(false); }}
    placeholder="Enter your answer"
    className={`w-full px-4 py-2.5 rounded-xl bg-[#12131f] border text-white placeholder-gray-600 focus:outline-none transition-colors text-sm ${
      captchaError ? "border-red-500/60" : "border-[#2a2d3a] focus:border-indigo-500"
    }`}
  />
  {captchaError && (
    <p className="text-red-400 text-xs mt-1.5">Incorrect answer — please try again.</p>
  )}
</div>
              <button
                type="submit"
                disabled={loading || !!emailError || !captchaAnswer}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? "Submitting…" : <>Request Demo <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// Node definitions — positions and connections
const NODES = [
  // Core hub
  { id: "nebula", label: "Nebula", sublabel: "AI Core", x: 500, y: 320, r: 52, type: "hub" },

  // Inner ring — treasury functions
  { id: "fx",          label: "FX & Hedging",      x: 500, y: 150, r: 38, type: "primary", color: "#6366f1" },
  { id: "debt",        label: "Debt",               x: 680, y: 230, r: 35, type: "primary", color: "#8b5cf6" },
  { id: "invest",      label: "Investments",        x: 720, y: 380, r: 35, type: "primary", color: "#06b6d4" },
  { id: "recv",        label: "Receivables",        x: 600, y: 490, r: 35, type: "primary", color: "#10b981" },
  { id: "pay",         label: "Payables",           x: 380, y: 490, r: 35, type: "primary", color: "#f59e0b" },
  { id: "cash",        label: "Cash",               x: 280, y: 380, r: 35, type: "primary", color: "#ec4899" },
  { id: "reporting",   label: "Reporting",          x: 310, y: 230, r: 35, type: "primary", color: "#a78bfa" },

  // Outer ring — data sources / systems
  { id: "erp",         label: "ERP",                x: 500,  y: 30,  r: 28, type: "outer", color: "#64748b" },
  { id: "banks",       label: "Banks",              x: 800,  y: 140, r: 28, type: "outer", color: "#64748b" },
  { id: "market",      label: "Market Data",        x: 870,  y: 340, r: 28, type: "outer", color: "#64748b" },
  { id: "accounting",  label: "Accounting",         x: 740,  y: 540, r: 28, type: "outer", color: "#64748b" },
  { id: "ap",          label: "AP / AR",            x: 240,  y: 540, r: 28, type: "outer", color: "#64748b" },
  { id: "cfo",         label: "CFO Dashboard",      x: 130,  y: 340, r: 28, type: "outer", color: "#64748b" },
  { id: "compliance",  label: "Compliance",         x: 190,  y: 140, r: 28, type: "outer", color: "#64748b" },
];

const EDGES = [
  // Hub to inner ring
  { from: "nebula", to: "fx",        animated: true },
  { from: "nebula", to: "debt",      animated: true },
  { from: "nebula", to: "invest",    animated: true },
  { from: "nebula", to: "recv",      animated: true },
  { from: "nebula", to: "pay",       animated: true },
  { from: "nebula", to: "cash",      animated: true },
  { from: "nebula", to: "reporting", animated: true },

  // Inner ring to outer sources
  { from: "fx",        to: "erp",        animated: false },
  { from: "fx",        to: "banks",      animated: false },
  { from: "fx",        to: "market",     animated: false },
  { from: "debt",      to: "banks",      animated: false },
  { from: "invest",    to: "market",     animated: false },
  { from: "recv",      to: "accounting", animated: false },
  { from: "pay",       to: "ap",         animated: false },
  { from: "cash",      to: "cfo",        animated: false },
  { from: "reporting", to: "compliance", animated: false },
  { from: "reporting", to: "cfo",        animated: false },
];

function getNode(id) {
  return NODES.find((n) => n.id === id);
}

// Animated pulse dot travelling along an edge
function PulseDot({ from, to, delay = 0 }) {
  const [pos, setPos] = useState(0);
  const animRef = useRef();
  const startRef = useRef(null);

  useEffect(() => {
    let rafId;
    const duration = 2200 + delay * 200;

    function animate(ts) {
      if (!startRef.current) startRef.current = ts - delay * 300;
      const elapsed = (ts - startRef.current) % duration;
      setPos(elapsed / duration);
      rafId = requestAnimationFrame(animate);
    }
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [delay]);

  const f = getNode(from);
  const t = getNode(to);
  if (!f || !t) return null;

  const x = f.x + (t.x - f.x) * pos;
  const y = f.y + (t.y - f.y) * pos;

  return (
    <circle cx={x} cy={y} r={3.5} fill="#a5b4fc" opacity={0.85}>
      <animate attributeName="opacity" values="0.85;0.3;0.85" dur="1.2s" repeatCount="indefinite" />
    </circle>
  );
}

export default function PlatformPage() {
  const [activeNode, setActiveNode] = useState(null);
  const [hovered,    setHovered]    = useState(null);
  const [showDemo,   setShowDemo]   = useState(false);

  const descriptions = {
    nebula:      "The Nebula AI Core orchestrates all treasury intelligence — connecting data, surfacing insights, and automating recommendations across every module.",
    fx:          "Live FX rates, natural hedge analysis, forward curve positioning, hedge ratio recommendations, and one-click deal capture.",
    debt:        "Full debt lifecycle management — drawdowns, repayments, covenant tracking, and refinancing trigger alerts.",
    invest:      "Short-term investment optimisation across money market funds, G-Secs, T-Bills, and bank deposits with yield benchmarking.",
    recv:        "DSO analytics, counterparty payment behaviour, cash flow forecasting, and receivables ageing intelligence.",
    pay:         "Payables scheduling, early payment discount analysis, supplier term optimisation, and cash impact modelling.",
    cash:        "Consolidated cash position across all banks and currencies, intraday liquidity tracking, and funding gap alerts.",
    reporting:   "AI-generated treasury reports, board packs, audit-ready exports, and regulatory compliance reporting.",
    erp:         "Bi-directional sync with SAP, Oracle, and Tally — positions, invoices, and GL entries flow in real time.",
    banks:       "Multi-bank connectivity via SWIFT, API, and MT940 — balances, transactions, and deal confirmations.",
    market:      "Live market data — FX spot, forward curves, interest rate benchmarks, and economic event calendar.",
    accounting:  "Automated journal entries, hedge accounting (IAS 39 / IFRS 9), and reconciliation exports.",
    ap:          "Accounts payable and receivable feeds — open items, payment runs, and ageing summaries.",
    cfo:         "Executive dashboard — net exposure, cash headroom, P&L impact, and key treasury KPIs.",
    compliance:  "RBI/FEMA (India), FCA (UK), and CBUAE (UAE) compliance flags, audit trails, and documentation.",
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      {/* Demo modal */}
      {showDemo && <DemoModal onClose={() => setShowDemo(false)} />}

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-white font-bold text-lg">N</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Nebula Finance AI</h1>
              <p className="text-xs text-gray-400 -mt-1">Finance Intelligence Platform</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/about" className="px-5 py-2.5 text-gray-300 hover:text-white transition-colors font-medium">
              About Us
            </Link>
            <Link
              to="/platform"
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium border border-indigo-400/30"
            >
              Build Your Platform
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-36 pb-8 px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-sm text-indigo-300 uppercase tracking-wider font-medium">
            Bespoke Enterprise Builds
          </span>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
          Built for Your Business.{" "}
          <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Owned by You.
          </span>
        </h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
          Nebula builds proprietary treasury and finance platforms for corporates — custom-designed
          across your departments, built by practitioners who have lived the problem.
          Click any node to explore what we can build for you.
        </p>
      </section>

      {/* Workflow diagram */}
      <section className="px-6 pb-8 flex flex-col items-center">
        <div className="w-full max-w-4xl relative">
          <svg
            viewBox="0 100 1000 530"
            className="w-full"
            style={{ filter: "drop-shadow(0 0 40px rgba(99,102,241,0.08))" }}
          >
            <defs>
              {/* Glow for hub */}
              <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
              </radialGradient>
              {/* Hub fill */}
              <radialGradient id="hubFill" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#4f46e5" />
                <stop offset="100%" stopColor="#3730a3" />
              </radialGradient>
            </defs>

            {/* Edges — outer (faint) */}
            {EDGES.filter((e) => !e.animated).map((e, i) => {
              const f = getNode(e.from);
              const t = getNode(e.to);
              return (
                <line
                  key={i}
                  x1={f.x} y1={f.y} x2={t.x} y2={t.y}
                  stroke="#2a2d3a"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                />
              );
            })}

            {/* Edges — hub spokes (glowing) */}
            {EDGES.filter((e) => e.animated).map((e, i) => {
              const f = getNode(e.from);
              const t = getNode(e.to);
              const isActive =
                activeNode === e.from ||
                activeNode === e.to ||
                hovered === e.from ||
                hovered === e.to;
              return (
                <line
                  key={`anim-${i}`}
                  x1={f.x} y1={f.y} x2={t.x} y2={t.y}
                  stroke={isActive ? "#818cf8" : "#3730a3"}
                  strokeWidth={isActive ? 1.5 : 1}
                  strokeOpacity={isActive ? 0.9 : 0.4}
                  style={{ transition: "all 0.3s" }}
                />
              );
            })}

            {/* Animated pulse dots on hub spokes */}
            {EDGES.filter((e) => e.animated).map((e, i) => (
              <PulseDot key={`dot-${i}`} from={e.from} to={e.to} delay={i} />
            ))}

            {/* Outer nodes */}
            {NODES.filter((n) => n.type === "outer").map((n) => (
              <g
                key={n.id}
                style={{ cursor: "pointer" }}
                onClick={() => setActiveNode(activeNode === n.id ? null : n.id)}
                onMouseEnter={() => setHovered(n.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <circle
                  cx={n.x} cy={n.y} r={n.r + 6}
                  fill="transparent"
                  stroke={activeNode === n.id ? "#6366f1" : "transparent"}
                  strokeWidth={1}
                />
                <circle
                  cx={n.x} cy={n.y} r={n.r}
                  fill="#12131a"
                  stroke={hovered === n.id || activeNode === n.id ? "#4f46e5" : "#2a2d3a"}
                  strokeWidth={1}
                  style={{ transition: "all 0.3s" }}
                />
                <text
                  x={n.x} y={n.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={hovered === n.id || activeNode === n.id ? "#a5b4fc" : "#6b7280"}
                  fontSize={9.5}
                  fontWeight={500}
                  fontFamily="system-ui, sans-serif"
                  style={{ transition: "fill 0.3s", userSelect: "none" }}
                >
                  {n.label}
                </text>
              </g>
            ))}

            {/* Primary nodes (inner ring) */}
            {NODES.filter((n) => n.type === "primary").map((n) => (
              <g
                key={n.id}
                style={{ cursor: "pointer" }}
                onClick={() => setActiveNode(activeNode === n.id ? null : n.id)}
                onMouseEnter={() => setHovered(n.id)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Glow ring */}
                <circle
                  cx={n.x} cy={n.y} r={n.r + 14}
                  fill="transparent"
                  stroke={n.color}
                  strokeWidth={0.5}
                  strokeOpacity={hovered === n.id || activeNode === n.id ? 0.5 : 0.15}
                  style={{ transition: "all 0.3s" }}
                />
                <circle
                  cx={n.x} cy={n.y} r={n.r}
                  fill="#0d0e18"
                  stroke={n.color}
                  strokeWidth={hovered === n.id || activeNode === n.id ? 1.5 : 0.8}
                  strokeOpacity={hovered === n.id || activeNode === n.id ? 1 : 0.6}
                  style={{ transition: "all 0.3s" }}
                />
                <text
                  x={n.x} y={n.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={hovered === n.id || activeNode === n.id ? "#ffffff" : "#c4c7d0"}
                  fontSize={10.5}
                  fontWeight={600}
                  fontFamily="system-ui, sans-serif"
                  style={{ transition: "fill 0.3s", userSelect: "none" }}
                >
                  {n.label}
                </text>
              </g>
            ))}

            {/* Hub — Nebula Core */}
            {(() => {
              const n = getNode("nebula");
              return (
                <g
                  key="hub"
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveNode(activeNode === "nebula" ? null : "nebula")}
                  onMouseEnter={() => setHovered("nebula")}
                  onMouseLeave={() => setHovered(null)}
                >
                  {/* Outer glow */}
                  <circle cx={n.x} cy={n.y} r={n.r + 30} fill="url(#hubGlow)" />
                  {/* Pulsing ring */}
                  <circle
                    cx={n.x} cy={n.y} r={n.r + 10}
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth={0.8}
                    strokeOpacity={0.4}
                  >
                    <animate attributeName="r" values={`${n.r + 8};${n.r + 18};${n.r + 8}`} dur="3s" repeatCount="indefinite" />
                    <animate attributeName="stroke-opacity" values="0.4;0;0.4" dur="3s" repeatCount="indefinite" />
                  </circle>
                  {/* Main circle */}
                  <circle cx={n.x} cy={n.y} r={n.r} fill="url(#hubFill)" stroke="#818cf8" strokeWidth={1.2} />
                  <text
                    x={n.x} y={n.y - 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="white"
                    fontSize={13}
                    fontWeight={700}
                    fontFamily="system-ui, sans-serif"
                    style={{ userSelect: "none" }}
                  >
                    {n.label}
                  </text>
                  <text
                    x={n.x} y={n.y + 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#a5b4fc"
                    fontSize={9}
                    fontFamily="system-ui, sans-serif"
                    style={{ userSelect: "none" }}
                  >
                    {n.sublabel}
                  </text>
                </g>
              );
            })()}
          </svg>
        </div>

        {/* Info panel */}
        <div
          className="w-full max-w-2xl min-h-[80px] mx-auto px-8 py-5 rounded-2xl border transition-all duration-300"
          style={{
            background: activeNode ? "rgba(99,102,241,0.07)" : "rgba(255,255,255,0.02)",
            borderColor: activeNode ? "rgba(99,102,241,0.3)" : "rgba(255,255,255,0.06)",
          }}
        >
          {activeNode ? (
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 mt-2 rounded-full bg-indigo-400 flex-shrink-0 animate-pulse" />
              <div>
                <p className="text-white font-semibold mb-1">
                  {NODES.find((n) => n.id === activeNode)?.label}
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {descriptions[activeNode]}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 text-sm text-center">
              Click any node to learn about that module or integration
            </p>
          )}
        </div>
      </section>

      {/* Feature highlights */}
      <section className="py-16 px-6 border-t border-[#2a2d3a]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "Practitioner-Led Development",
              desc: "Your platform is scoped and overseen by treasury practitioners — not project managers reading a spec. Every screen reflects how treasury actually works.",
              color: "indigo",
            },
            {
              title: "You Own It. Completely.",
              desc: "No licensing fees, no vendor lock-in. We build it, you own the codebase, the data, and the infrastructure. Your IP, your terms.",
              color: "purple",
            },
            {
              title: "Any Department. Any Region.",
              desc: "Treasury, AP, AR, FX, Debt, Investments, Accounting — built for India (INR/RBI), UK (GBP/FCA), or UAE (AED with Islamic finance support).",
              color: "cyan",
            },
          ].map((f) => (
            <div key={f.title} className="nebula-card p-6">
              <div className={`w-2 h-8 rounded-full bg-${f.color}-500 mb-5`} />
              <h3 className="text-white font-bold mb-2">{f.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">Ready to Start a Conversation?</h2>
        <p className="text-gray-400 mb-10 max-w-xl mx-auto">
          Tell us what you want to build. A treasury practitioner will come back to you within
          one business day.
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <button
            onClick={() => setShowDemo(true)}
            className="nebula-btn-primary inline-flex items-center gap-2 px-8 py-4 text-lg"
          >
            Discuss Your Build <CalendarCheck className="w-5 h-5" />
          </button>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-8 py-4 border border-gray-700 hover:border-indigo-500 rounded-xl text-white font-medium transition-all hover:bg-indigo-500/10 text-lg"
          >
            Access Platform <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#2a2d3a]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-white font-bold">N</span>
            </div>
            <div>
              <p className="text-white font-semibold">Nebula Finance AI</p>
              <p className="text-gray-500 text-sm">Excellence in Financial Solutions</p>
            </div>
          </div>
          <p className="text-gray-500 text-sm">© 2026 Nebula Finance AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
